/**
 * UpcomingWeather — Sprint 5 Enhancement · Current Weather on Upcoming Itinerary
 *
 * Renders a minimal weather chip (icon + temperature) at the right of
 * every destination-based itinerary row. Data source is Open-Meteo —
 * free, CORS-enabled, no API key required. We ask for `current` and
 * `daily` in a single request and render:
 *
 *   ☀️ 31° / 26°     ← icon + today's high / low
 *
 * or fall back to the current temperature alone if the daily block is
 * absent:
 *
 *   ☀️ 24°
 *
 * If the fetch fails or coords are missing, the component renders
 * NOTHING (no placeholder, no dash). Silence beats noise here — the
 * itinerary must remain scannable.
 *
 * A module-level cache dedupes requests by port_id so five rows for
 * the same port issue exactly one network call.
 */
import React, { useEffect, useState } from "react";

// WMO weather code → emoji glyph. Kept intentionally minimal; the icon
// is the sole condition indicator (no "Sunny" / "Partly Cloudy" text).
// Reference: https://open-meteo.com/en/docs (WMO Weather interpretation).
function wmoEmoji(code) {
  if (code == null) return null;
  if (code === 0) return "\u2600\uFE0F";                   // clear
  if (code === 1 || code === 2) return "\u{1F324}\uFE0F"; // mostly clear / partly cloudy
  if (code === 3) return "\u2601\uFE0F";                   // overcast
  if (code === 45 || code === 48) return "\u{1F32B}\uFE0F"; // fog
  if (code >= 51 && code <= 57) return "\u{1F326}\uFE0F";   // drizzle
  if (code >= 61 && code <= 67) return "\u{1F327}\uFE0F";   // rain
  if (code >= 71 && code <= 77) return "\u{1F328}\uFE0F";   // snow
  if (code >= 80 && code <= 82) return "\u{1F326}\uFE0F";   // rain showers
  if (code >= 85 && code <= 86) return "\u{1F328}\uFE0F";   // snow showers
  if (code >= 95 && code <= 99) return "\u26C8\uFE0F";      // thunderstorm
  return null;
}

// Module-level cache keyed by "portId::unit" — persists for the
// lifetime of the tab. Weather changes slowly, and travelers scan the
// itinerary repeatedly, so we don't want a network hit per row per
// scroll.
const _cache = new Map();
const _inflight = new Map();

async function fetchWeather({ lat, lng, unit }) {
  const tempUnit = unit === "imperial" ? "fahrenheit" : "celsius";
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", lat.toFixed(4));
  url.searchParams.set("longitude", lng.toFixed(4));
  url.searchParams.set("current", "temperature_2m,weather_code");
  url.searchParams.set("daily", "temperature_2m_max,temperature_2m_min");
  url.searchParams.set("temperature_unit", tempUnit);
  url.searchParams.set("timezone", "auto");
  const r = await fetch(url.toString());
  if (!r.ok) throw new Error(`Open-Meteo HTTP ${r.status}`);
  const data = await r.json();
  const current = data.current || {};
  const daily = data.daily || {};
  return {
    temp: Math.round(current.temperature_2m ?? NaN),
    code: current.weather_code ?? null,
    high: Array.isArray(daily.temperature_2m_max) ? Math.round(daily.temperature_2m_max[0]) : null,
    low:  Array.isArray(daily.temperature_2m_min) ? Math.round(daily.temperature_2m_min[0]) : null,
    unit: tempUnit === "fahrenheit" ? "F" : "C",
  };
}

/**
 * @param {object}  props
 * @param {string}  props.portId        · dedupes network requests
 * @param {number}  props.lat
 * @param {number}  props.lng
 * @param {string} [props.unit]         · "metric" | "imperial" — defaults metric
 * @param {string} [props.size]         · "compact" (default) | "prominent"
 */
export default function UpcomingWeather({ portId, lat, lng, unit = "metric", size = "compact" }) {
  const [data, setData] = useState(() => _cache.get(`${portId}::${unit}`) || null);

  useEffect(() => {
    if (!portId || typeof lat !== "number" || typeof lng !== "number") return;
    const key = `${portId}::${unit}`;
    if (_cache.has(key)) {
      setData(_cache.get(key));
      return;
    }
    if (_inflight.has(key)) {
      _inflight.get(key).then(setData).catch(() => setData(null));
      return;
    }
    const p = fetchWeather({ lat, lng, unit })
      .then((d) => { _cache.set(key, d); _inflight.delete(key); return d; })
      .catch(() => { _inflight.delete(key); return null; });
    _inflight.set(key, p);
    let alive = true;
    p.then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, [portId, lat, lng, unit]);

  // Silent hide — no placeholders, per PO directive.
  if (!data || !Number.isFinite(data.temp)) return null;

  const glyph = wmoEmoji(data.code);
  const showRange = Number.isFinite(data.high) && Number.isFinite(data.low);

  // RC7-DASHBOARD-032 · Prominent variant recovers the header space
  // freed by retiring the per-card Explore action. Larger glyph,
  // larger temperature typography, temperature stacked under the
  // icon so the high/low reads at a glance without ever competing
  // with the port name.
  if (size === "prominent") {
    return (
      <div
        className="shrink-0 flex flex-col items-end leading-tight text-slate-700 tabular-nums pl-3"
        data-testid={`upcoming-weather-${portId}`}
        aria-label={`Current weather at ${portId}: ${data.temp} degrees ${data.unit}`}
        title="Current weather"
      >
        {glyph && (
          <span className="text-[26px] leading-none mb-0.5" aria-hidden>
            {glyph}
          </span>
        )}
        <span className="font-display text-[15px] font-semibold">
          {showRange ? `${data.high}° / ${data.low}°` : `${data.temp}°`}
        </span>
      </div>
    );
  }

  return (
    <div
      className="shrink-0 flex items-center gap-1.5 font-body text-[12px] text-slate-500 tabular-nums pl-3"
      data-testid={`upcoming-weather-${portId}`}
      aria-label={`Current weather at ${portId}: ${data.temp} degrees ${data.unit}`}
      title="Current weather"
    >
      {glyph && (
        <span className="text-[15px] leading-none" aria-hidden>
          {glyph}
        </span>
      )}
      <span>
        {showRange ? `${data.high}° / ${data.low}°` : `${data.temp}°`}
      </span>
    </div>
  );
}
